import { useSearchParams } from "react-router-dom"
import { useCallback, useMemo, useState } from "react"
import { INSTRUMENT_TYPES } from "resources/instruments"

type UrlInstrumentType = keyof typeof INSTRUMENT_TYPES
export interface UrlInstrument {
  urlInstrumentType: UrlInstrumentType | null
  urlInstrumentId: string | null
}

interface InstrumentParams {
  instrument: UrlInstrument
  clearInstrumentFromUrl: () => void
}

const EMPTY: UrlInstrument = {
  urlInstrumentType: null,
  urlInstrumentId: null,
}

export function useGetUrlInstrumentParams(): InstrumentParams {
  const [searchParams, setSearchParams] = useSearchParams()
  const [lastValid, setLastValid] = useState<UrlInstrument>(EMPTY)

  const derivedFromUrl = useMemo(() => {
    const type = searchParams.get("instrumentType") as UrlInstrumentType
    const id = searchParams.get("instrumentId")
    if (id && type in INSTRUMENT_TYPES) {
      return { urlInstrumentType: type, urlInstrumentId: id }
    }
    return null
  }, [searchParams])

  const instrument = derivedFromUrl ?? lastValid

  const clearInstrumentFromUrl = useCallback(() => {
    const type = searchParams.get("instrumentType") as UrlInstrumentType
    const id = searchParams.get("instrumentId")
    if (!type && !id) return
    if (id && type in INSTRUMENT_TYPES) {
      setLastValid({ urlInstrumentType: type, urlInstrumentId: id })
    } else {
      setLastValid(EMPTY)
    }
    const newParams = new URLSearchParams(searchParams)
    newParams.delete("instrumentType")
    newParams.delete("instrumentId")
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  return { instrument, clearInstrumentFromUrl }
}
